import React, { useMemo, useState } from 'react';
import { Container, Row, Col, Table, Button, Form, InputGroup, Badge } from 'react-bootstrap';
import { useOutletContext } from 'react-router-dom';
import { useEditProducts } from '../Context/EditProductContext';
import MakeRequest from '../Helpers/MakeRequest';

// A product only gets an _id once it has been saved, so an unsaved row is
// identified by its position in the (append-only) products array.
const keyOf = (product, idx) => product._id ?? `new-${idx}`;

const ProductRows = () => {
    const { l } = useOutletContext();
    const { state, updateProduct, removeProduct, setSaveDisabled } = useEditProducts();
    const { productTypes, products } = state;

    const [search, setSearch] = useState('');
    const [sortAsc, setSortAsc] = useState(true);
    const [openMap, setOpenMap] = useState({});

    // Saved products start collapsed; a freshly added one starts open so it can be filled in.
    const isOpen = (product, idx) => openMap[keyOf(product, idx)] ?? !product._id;

    const toggleOpen = (product, idx) => {
        const key = keyOf(product, idx);
        const next = !isOpen(product, idx);
        setOpenMap(prev => ({ ...prev, [key]: next }));
    };

    const handleDeleteProduct = async (idx) => {
        if (!products[idx]._id) {
            removeProduct(idx);
            return;
        }
        if (window.confirm(l("Är du säker på att du vill ta bort denna produkt?", "Are you sure you want to delete this product?"))) {
            try {
                await MakeRequest(null, `content/products/${products[idx]._id}`, 'delete');
                removeProduct(idx);
            } catch (error) {
                console.error("Failed to delete product:", error);
            }
        }
    };

    const handleFileChange = (e, idx, field) => {
        setSaveDisabled(false);
        if (field === 'safetySheets') {
            const files = Array.from(e.target.files);

            for (const file of files) {
                if (file.type !== "application/pdf") {
                    alert(l("Endast PDF-filer är tillåtna för säkerhetsblad.", "Only PDF files are allowed for safety data sheets."));
                    return;
                }
                const reader = new FileReader();
                reader.onloadend = () => {
                    const newProducts = [...products];
                    newProducts[idx].safetySheets = [...(newProducts[idx].safetySheets || []), { file: reader.result, name: file.name, titleSv: '', titleEn: '' }];
                    updateProduct(idx, { safetySheets: newProducts[idx].safetySheets });
                }
                reader.readAsDataURL(file);
            }
            return;
        }

        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                const newProducts = [...products];
                newProducts[idx].imageSrc = {
                    file: reader.result,
                    name: file.name
                };
                updateProduct(idx, { imageSrc: newProducts[idx].imageSrc });
            };
            reader.readAsDataURL(file);
        };
    };

    const handleDeleteSheet = async (productIdx, fileIdx) => {

        if (products[productIdx].safetySheets && products[productIdx].safetySheets[fileIdx]?.file?.startsWith("data:")) {
            const newProducts = [...products];
            newProducts[productIdx].safetySheets = newProducts[productIdx].safetySheets.filter((_, i) => i !== fileIdx);
            updateProduct(productIdx, { safetySheets: newProducts[productIdx].safetySheets });
            setSaveDisabled(false);
            return;
        }
        if (window.confirm(l("Är du säker på att du vill ta bort detta säkerhetsblad?", "Are you sure you want to delete this safety data sheet?"))) {
            const newProducts = [...products];
            const sheetToDelete = newProducts[productIdx].safetySheets[fileIdx];
            try {
                const res = await MakeRequest(null, `content/products/${products[productIdx]._id}/safetySheets?name=${sheetToDelete.name}`, 'delete');
                if (res.status !== 200) {
                    alert(l("Misslyckades med att ta bort säkerhetsbladet.", "Failed to delete the safety data sheet."));
                    return;
                }
                newProducts[productIdx].safetySheets = newProducts[productIdx].safetySheets.filter((_, i) => i !== fileIdx);
                updateProduct(productIdx, { safetySheets: newProducts[productIdx].safetySheets });
            } catch (error) {
                console.error("Failed to delete safety sheet:", error);
            }
        }
    };

    const handleFileTitleChange = (e, productIdx, fileIdx, titleField) => {
        setSaveDisabled(false);
        const newProducts = [...products];
        newProducts[productIdx].safetySheets[fileIdx][titleField] = e.target.value;
        updateProduct(productIdx, { safetySheets: newProducts[productIdx].safetySheets });
    };

    // Every mutation helper addresses products by their index in state.products,
    // so the original index has to survive filtering and sorting.
    const visible = useMemo(() => {
        const entries = (products ?? []).map((product, idx) => ({ product, idx }));
        const isUnsaved = ({ product }) => !product._id;

        const query = search.trim().toLowerCase();
        const matches = ({ product }) => {
            if (!query) return true;
            return [
                product.name,
                product.type?.typeSwe,
                product.type?.typeEng,
                product.category?.categorySwe,
                product.category?.categoryEng,
            ].some(field => typeof field === 'string' && field.toLowerCase().includes(query));
        };

        const saved = entries
            .filter(entry => !isUnsaved(entry) && matches(entry))
            .sort((a, b) => (sortAsc ? 1 : -1) *
                (a.product.name ?? '').localeCompare(b.product.name ?? '', 'sv', { sensitivity: 'base' }));

        // Unsaved rows are pinned to the end and ignore the filter, otherwise a product
        // added while a search is active would vanish before it could be named.
        return [...saved, ...entries.filter(isUnsaved)];
    }, [products, search, sortAsc]);

    const anyOpen = visible.some(({ product, idx }) => isOpen(product, idx));

    const toggleAll = () => {
        const next = {};
        visible.forEach(({ product, idx }) => { next[keyOf(product, idx)] = !anyOpen; });
        setOpenMap(prev => ({ ...prev, ...next }));
    };

    const savedCount = (products ?? []).filter(p => p._id).length;

    return (
        <Col xs={12} className='p-0'>

            {/* Search / sort toolbar */}
            <Row className='m-0 p-0 g-2 align-items-center'>
                <Col xl={5} md={6} xs={12} className='px-0'>
                    <InputGroup>
                        <InputGroup.Text><i className="fa-solid fa-magnifying-glass"></i></InputGroup.Text>
                        <Form.Control
                            type="search"
                            placeholder={l("Sök produkt...", "Search product...")}
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                        {search && (
                            <Button
                                variant="outline-secondary"
                                title={l("Rensa sökning", "Clear search")}
                                onClick={() => setSearch('')}
                            >
                                <i className="fa-solid fa-times"></i>
                            </Button>
                        )}
                    </InputGroup>
                </Col>
                <Col xs="auto">
                    <Button
                        variant="outline-secondary"
                        onClick={() => setSortAsc(v => !v)}
                        title={l("Sortera efter namn", "Sort by name")}
                    >
                        <i className={`fa-solid ${sortAsc ? 'fa-arrow-down-a-z' : 'fa-arrow-up-z-a'}`}></i>
                        <span className='ms-2'>{l("Namn", "Name")}</span>
                    </Button>
                </Col>
                <Col xs="auto">
                    <Button variant="outline-secondary" onClick={toggleAll}>
                        {anyOpen ? l("Fäll ihop alla", "Collapse all") : l("Expandera alla", "Expand all")}
                    </Button>
                </Col>
                <Col xs="auto" className='text-muted'>
                    {search.trim()
                        ? l(`${visible.filter(({ product }) => product._id).length} av ${savedCount} produkter`,
                            `${visible.filter(({ product }) => product._id).length} of ${savedCount} products`)
                        : l(`${savedCount} produkter`, `${savedCount} products`)}
                </Col>
            </Row>

            {visible.length === 0 && (
                <div style={{ color: "#888", fontStyle: "italic", padding: "20px 0" }}>
                    {l("Inga produkter matchar sökningen.", "No products match your search.")}
                </div>
            )}

            {visible.map(({ product, idx }) => {
                const open = isOpen(product, idx);
                const typeLabel = l(product.type?.typeSwe, product.type?.typeEng);
                const categoryLabel = l(product.category?.categorySwe, product.category?.categoryEng);

                return (
        <Container key={keyOf(product, idx)} style={{ border: "1px solid #ccc", marginBottom: "10px", borderRadius: "5px", backgroundColor: "white" }} className='p-0 m-0 mt-3'>

            {/* Collapsed summary — click to toggle the detailed content */}
            <Row
                className='m-0 p-2 align-items-center'
                style={{ cursor: "pointer", backgroundColor: open ? "#f1f3f5" : "white", borderRadius: "5px" }}
                onClick={() => toggleOpen(product, idx)}
                role="button"
                aria-expanded={open}
            >
                <Col className='d-flex align-items-center flex-wrap gap-2 p-0'>
                    <i
                        className={`fa-solid ${open ? 'fa-chevron-down' : 'fa-chevron-right'}`}
                        style={{ width: 14, color: "#666" }}
                    ></i>
                    <b>{product.name?.trim() || l("(Namnlös produkt)", "(Unnamed product)")}</b>
                    {product.prioritized && (
                        <i className="fa-solid fa-star" style={{ color: "#e0a800" }} title={l("Prioriterad", "Prioritized")}></i>
                    )}
                    {typeLabel && <Badge bg="secondary" className='fw-normal'>{typeLabel}</Badge>}
                    {categoryLabel && <Badge bg="light" text="dark" className='fw-normal'>{categoryLabel}</Badge>}
                    {!product._id && <Badge bg="success" className='fw-normal'>{l("Ny", "New")}</Badge>}
                </Col>
                <Col xs="auto" className='p-0'>
                    <Button
                        size="sm"
                        variant="outline-danger"
                        title={l("Ta bort produkt", "Delete product")}
                        onClick={e => { e.stopPropagation(); handleDeleteProduct(idx); }}
                    >
                        <i className="fa-solid fa-trash"></i>
                    </Button>
                </Col>
            </Row>

            {open && (
            <div style={{ padding: "10px" }}>

            {/* Product names, type, image */}
            <Row className='m-0 p-0'>
                <Col xl={4} md={12} style={{ border: "1px solid lightgrey" }} className='p-2'>
                    <Form.Label><b>{l("Produktnamn", "Product name")}</b></Form.Label>
                    <Form.Control
                        type="text"
                        value={product.name ?? ""}
                        onChange={(e) => {
                            setSaveDisabled(false);
                            updateProduct(idx, { name: e.target.value });
                        }}
                    />
                </Col>
                <Col xl={4} md={12} style={{ border: "1px solid lightgrey" }} className='p-2'>
                    <Form.Label><b>{l("Typ/Kategori", "Type/Category")}</b></Form.Label>
                    <Form.Select
                        value={l(product.type?.typeSwe, product.type?.typeEng) || ''}
                        onChange={e => {
                            setSaveDisabled(false);
                            const rightType = productTypes.find(pt => l(pt.typeSwe, pt.typeEng) === e.target.value);
                            const type = {
                                typeSwe: rightType?.typeSwe || '',
                                typeEng: rightType?.typeEng || '',
                            };
                            updateProduct(idx, { type, category: null });
                        }}
                    >
                        <option value="">{l("Välj produkttyp", "Select product type")}</option>
                        {productTypes.filter(pt => pt.typeSwe !== "" && pt.typeEng !== "").map((pt, i) => (
                            <option key={i} value={l(pt.typeSwe, pt.typeEng)}>{l(pt.typeSwe, pt.typeEng)}</option>
                        ))}
                    </Form.Select>
                    <Form.Select
                        disabled={!product.type}
                        className='mt-2'
                        value={l(product.category?.categorySwe, product.category?.categoryEng) || ''}
                        onChange={e => {
                            setSaveDisabled(false);
                            const rightType = productTypes.find(pt => l(pt.typeSwe, pt.typeEng) === l(product.type?.typeSwe, product.type?.typeEng));
                            const rightCategory = rightType?.subcategories?.find(sc => l(sc.categorySwe, sc.categoryEng) === e.target.value);
                            updateProduct(idx, { category: rightCategory });
                        }}
                    >
                        <option value="">{l("Välj underkategori", "Select subcategory")}</option>
                        {productTypes.find(pt => l(pt.typeSwe, pt.typeEng) === l(product.type?.typeSwe, product.type?.typeEng))?.subcategories?.map((sc, i) => (
                            <option key={i} value={l(sc.categorySwe, sc.categoryEng)}>{l(sc.categorySwe, sc.categoryEng)}</option>
                        ))}
                    </Form.Select>
                    <Form.Label className='mt-4'><b>{l("Prioriterad", "Prioritized")}</b></Form.Label>
                    <Form.Check
                        type="switch"
                        checked={!!product.prioritized}
                        id={`prioritized-switch-${idx}`}
                        label="" // optional, leave empty if you already have the Form.Label
                        onChange={() => {
                            setSaveDisabled(false);
                            updateProduct(idx, { prioritized: !product.prioritized})
                        }}
                    />
                </Col>
                <Col xl={4} md={12} style={{ border: "1px solid lightgrey" }} className='p-2'>
                    <Form.Label><b>{l("Bild", "Image")}</b></Form.Label>
                    {product.imageSrc?.file && typeof product.imageSrc?.file === "string" && (
                        <div style={{ marginTop: 5 }}>
                            <img src={product.imageSrc.file} alt="preview" style={{ maxWidth: 200, maxHeight: 150 }} />
                        </div>
                    )}
                    <Form.Control
                        type="file"
                        accept="image/*"
                        className='mt-3'
                        onChange={e => handleFileChange(e, idx, 'image')}
                    />
                </Col>
            </Row>

            {/* Content*/}
            <Row className='m-0 p-0'>
                <Col xl={6} md={12} style={{ border: "1px solid lightgrey" }} className='p-2'>
                    <Form.Label><b>{l("Innehåll (Svenska)", "Content (Swedish)")}</b></Form.Label>
                    <Form.Control
                        as="textarea"
                        style={{ height: '200px' }}
                        value={product.contentSwe || ''}
                        onChange={e => {
                            setSaveDisabled(false);
                            updateProduct(idx, { contentSwe: e.target.value });
                        }}
                    />
                </Col>
                <Col xl={6} md={12} style={{ border: "1px solid lightgrey" }} className='p-2'>
                    <Form.Label><b>{l("Innehåll (Engelska)", "Content (English)")}</b></Form.Label>
                    <Form.Control
                        as="textarea"
                        style={{ height: '200px' }}
                        value={product.contentEng || ''}
                        onChange={e => {
                            setSaveDisabled(false);
                            updateProduct(idx, { contentEng: e.target.value });
                        }}
                    />
                </Col>
            </Row>

            {/* Safety sheets */}
            <Row className='m-0 p-0'>
                <Row className='m-0 p-0 my-2'>
                    <Container>
                        <Form.Label><b>{l("Säkerhetsblad", "Safety Data Sheet")}
                        </b></Form.Label>

                        <input
                            id={`file-input-${idx}`}
                            type="file"
                            accept="application/pdf"
                            multiple
                            style={{ display: "none" }}
                            onChange={e => handleFileChange(e, idx, "safetySheets")}
                        />

                        <label htmlFor={`file-input-${idx}`}>
                            <Button
                                as="span"
                                size="sm"
                                style={{ marginLeft: 10 }}
                                title={l("Ladda upp PDF", "Upload PDF")}
                            >
                                <i className="fa-solid fa-plus"></i>
                            </Button>
                        </label>
                    </Container>
                </Row>

                <Row className='m-0 p-0'>
                    <Col>
                        {Array.isArray(product.safetySheets) && product.safetySheets.length > 0 ? (
                            <>
                                <Table striped bordered hover className="stack-on-md">
                                    <thead>
                                        <tr>
                                            <th style={{ fontWeight: "unset" }}>{l("Fil", "File")}</th>
                                            <th style={{ fontWeight: "unset" }}>{l("Titel Svenska", "Title Swedish")}</th>
                                            <th style={{ fontWeight: "unset" }}>{l("Titel Engelska", "Title English")}</th>
                                            <th style={{ fontWeight: "unset" }}>{l("Typ", "Type")}</th>
                                            <th style={{ fontWeight: "unset" }}>{l("Språk", "Language")}</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {product.safetySheets.map((file, fileIdx) => (
                                            <tr key={fileIdx}>
                                                <td>
                                                    {typeof file === 'string' ? file.split("-").slice(2).join("-") : file.name}
                                                </td>
                                                <td><Form.Control type="text" value={file.titleSv} onChange={e => handleFileTitleChange(e, idx, fileIdx, 'titleSv')} /></td>
                                                <td><Form.Control type="text" value={file.titleEn} onChange={e => handleFileTitleChange(e, idx, fileIdx, 'titleEn')} /></td>
                                                <td>
                                                    <Form.Select value={file.type} onChange={e => handleFileTitleChange(e, idx, fileIdx, 'type')}>
                                                        <option value="">{l("Välj typ", "Select type")}</option>
                                                        <option value="safetysheet">{l("Säkerhetsdatablad", "Safety Datasheet")}</option>
                                                        <option value="safetysheet-hardener">{l("Säkerhetsdatablad härdare", "Safety Datasheet Hardener")}</option>
                                                        <option value="technical-datasheet">{l("Tekniskt Datablad", "Technical Datasheet")}</option>
                                                        <option value="productsheet">{l("Produktblad", "Product sheet")}</option>
                                                        <option value="other">{l("Övrigt", "Other")}</option>
                                                    </Form.Select>
                                                </td>
                                                <td>
                                                    <Form.Select value={file.language} onChange={e => handleFileTitleChange(e, idx, fileIdx, 'language')}>
                                                        <option value="">{l("Välj språk", "Select language")}</option>
                                                        <option value="swedish">{l("Svenska", "Swedish")}</option>
                                                        <option value="english">{l("Engelska", "English")}</option>
                                                        <option value="spanish">{l("Spanska", "Spanish")}</option>
                                                        <option value="norwegian">{l("Norska", "Norwegian")}</option>
                                                        <option value="finnish">{l("Finska", "Finnish")}</option>
                                                    </Form.Select>
                                                </td>
                                                <td>
                                                    <Button
                                                        variant="link"
                                                        onClick={() => handleDeleteSheet(idx, fileIdx)}
                                                    >
                                                        <i className="fa-solid fa-times" style={{ color: "red" }}></i>
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </>
                        ) : (<div style={{ color: "#888", fontStyle: "italic", padding: "10px 0" }}>
                            {l("Inga säkerhetsblad har lagts till.", "No safety data sheets have been added.")}
                        </div>)}
                    </Col>
                </Row>
            </Row>

            </div>
            )}

        </Container>
                );
            })}
        </Col>
    );
};

export default ProductRows;